import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  metarConfigService,
  MOIS_LABELS,
  type UpdateMetarConfigurationDTO,
} from '@/services/metarConfigService';
import { useAuthStore } from '@/store/authStore';
import toast from 'react-hot-toast';
import { CloudIcon, ChartBarIcon } from '@heroicons/react/24/outline';

const PERIODES = [
  { value: 'jour' as const, label: "Aujourd'hui" },
  { value: 'mois' as const, label: 'Ce mois-ci' },
];

/**
 * Parametrage du debit d'appels a l'API METAR externe (par saison) et
 * compteur d'appels pour facturation.
 *
 * Voir "SFD - Parametrage des appels METAR" (RG-MET-01 a RG-MET-07).
 * Lecture ouverte a ROLE_ADMIN et ROLE_GID_REGUL ; modification reservee a
 * ROLE_ADMIN (impact direct sur le cout facture par le fournisseur).
 */
export function MetarConfigurationPage() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const peutModifier = user?.roles.includes('ROLE_ADMIN') ?? false;

  const [periode, setPeriode] = useState<'jour' | 'mois'>('jour');
  const [formData, setFormData] = useState<UpdateMetarConfigurationDTO>({});

  const { data: config, isLoading } = useQuery({
    queryKey: ['metar-configuration'],
    queryFn: () => metarConfigService.getConfiguration(),
  });

  const { data: compteur } = useQuery({
    queryKey: ['metar-compteur', periode],
    queryFn: () => metarConfigService.getCompteur(periode),
    refetchInterval: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (config) {
      setFormData({
        appelsParHeureHiver: config.appelsParHeureHiver,
        appelsParHeureEte: config.appelsParHeureEte,
        moisHiver: config.moisHiver,
      });
    }
  }, [config]);

  const updateMutation = useMutation({
    mutationFn: (data: UpdateMetarConfigurationDTO) => metarConfigService.updateConfiguration(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metar-configuration'] });
      toast.success('Paramétrage METAR mis à jour');
    },
    onError: () => toast.error('Erreur lors de la mise à jour'),
  });

  const toggleMoisHiver = (mois: number) => {
    setFormData((prev) => {
      const current = prev.moisHiver || [];
      const next = current.includes(mois)
        ? current.filter((m) => m !== mois)
        : [...current, mois];
      return { ...prev, moisHiver: next };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.moisHiver || formData.moisHiver.length === 0) {
      toast.error('Au moins un mois hiver doit être sélectionné');
      return;
    }
    updateMutation.mutate(formData);
  };

  const nombreAeroports = 2; // ORY et CDG - RG-MET-03
  const plafondHiver = (formData.appelsParHeureHiver || 0) * nombreAeroports;
  const plafondEte = (formData.appelsParHeureEte || 0) * nombreAeroports;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        <p className="text-sm text-gray-500 mt-3">Chargement du paramétrage METAR...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CloudIcon className="w-7 h-7 text-primary-500" />
          Paramétrage des appels METAR
        </h2>
      </div>

      {/* Formulaire de parametrage */}
      <div className="card">
        <div className="card-body">
          <h3 className="font-semibold mb-1">Débit d'appels par saison</h3>
          <p className="text-sm text-gray-500 mb-4">
            Limite le nombre d'appels réels à l'API externe metar-taf.com, par aéroport.
            Plafond réel total = cette valeur × {nombreAeroports} aéroports (ORY et CDG).
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Appels / heure en hiver</label>
                <input
                  type="number"
                  min={1}
                  className="input"
                  value={formData.appelsParHeureHiver ?? ''}
                  disabled={!peutModifier}
                  onChange={(e) =>
                    setFormData({ ...formData, appelsParHeureHiver: parseInt(e.target.value, 10) || 0 })
                  }
                />
                <p className="text-xs text-gray-500 mt-1">Plafond réel : {plafondHiver} appels/heure (2 aéroports)</p>
              </div>
              <div>
                <label className="label">Appels / heure en été</label>
                <input
                  type="number"
                  min={1}
                  className="input"
                  value={formData.appelsParHeureEte ?? ''}
                  disabled={!peutModifier}
                  onChange={(e) =>
                    setFormData({ ...formData, appelsParHeureEte: parseInt(e.target.value, 10) || 0 })
                  }
                />
                <p className="text-xs text-gray-500 mt-1">Plafond réel : {plafondEte} appels/heure (2 aéroports)</p>
              </div>
            </div>

            <div>
              <label className="label">Mois considérés comme "hiver"</label>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {Object.entries(MOIS_LABELS).map(([mois, label]) => (
                  <label
                    key={mois}
                    className="flex items-center gap-2 px-2 py-1.5 border border-gray-200 rounded-lg text-sm cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={(formData.moisHiver || []).includes(Number(mois))}
                      disabled={!peutModifier}
                      onChange={() => toggleMoisHiver(Number(mois))}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {!peutModifier && (
              <p className="text-xs text-amber-600">
                Lecture seule - seul un administrateur peut modifier ce paramétrage.
              </p>
            )}

            {peutModifier && (
              <button type="submit" className="btn-primary" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            )}
          </form>
        </div>
      </div>

      {/* Compteur d'appels (facturation) */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <ChartBarIcon className="w-5 h-5 text-primary-500" />
              Compteur d'appels (facturation)
            </h3>
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
              {PERIODES.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPeriode(p.value)}
                  className={`px-3 py-1.5 text-sm font-medium ${
                    periode === p.value ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {compteur && (
            <>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{compteur.totaux.total}</div>
                  <div className="text-xs text-gray-500">Appels totaux</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-700">{compteur.totaux.reussis}</div>
                  <div className="text-xs text-gray-500">Réussis</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-700">{compteur.totaux.echecs}</div>
                  <div className="text-xs text-gray-500">Échecs</div>
                </div>
              </div>

              {compteur.parJour.length > 0 ? (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2">Jour</th>
                      <th className="py-2 text-right">Total</th>
                      <th className="py-2 text-right">Réussis</th>
                      <th className="py-2 text-right">Échecs</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {compteur.parJour.map((j) => (
                      <tr key={j.jour}>
                        <td className="py-2">{j.jour}</td>
                        <td className="py-2 text-right">{j.total}</td>
                        <td className="py-2 text-right text-green-700">{j.reussis}</td>
                        <td className="py-2 text-right text-red-700">{j.echecs}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-gray-500">Aucun appel enregistré sur cette période.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MetarConfigurationPage;
